import CreationModalContainer from '@app/src/components/CreationModal/CreationModalContainer'
import CreationModalStep from '@app/src/components/CreationModal/CreationModalStep'
import { useCreationModalProgress } from '@app/src/context/CreationModalProgressContext'
import { useProvidersModal } from '@app/src/context/ProvidersModalContext'
import { toError } from '@app/src/errors/toError'
import sentryService from '@app/src/services/SentryService'
import { canProviderBeEnriched } from '@app/src/utils/providerEnrichmentUtils'
import { Dialog, Stack } from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import EnrichmentEligibilityStep from './EnrichmentEligibilityStep'
import EnrichmentInfoStep from './EnrichmentInfoStep'
import EnrichmentSuccessView from './EnrichmentSuccessView'
import { useScheduleEnrichment } from './hooks/scheduleEnrichment'

export enum SCHEDULE_ENRICHMENT_QUESTION_KEYS {
  PROVIDER_IDS = 'providerIds',
  SEND_EMAIL_ON_COMPLETE = 'sendEmailOnComplete',
}

type ScheduleEnrichmentModalProps = {
  open: boolean
  onClose: () => void
}

export type ScheduleEnrichmentFormData = {
  providerIds: number[]
  sendEmailOnComplete: boolean
}

const WIDTH = 584

const ScheduleEnrichmentModal: React.FC<ScheduleEnrichmentModalProps> = ({ open, onClose }) => {
  const { formatMessage } = useIntl()
  const { activeStep, setActiveStep, setTotalSteps } = useCreationModalProgress()
  const { providersToConnect } = useProvidersModal()
  const { scheduleEnrichment, isScheduling } = useScheduleEnrichment()
  const { canScheduleMultipleEnrichments = false } = useFlags()
  const [showSuccessScreen, setShowSuccessScreen] = useState(false)

  const formMethods = useForm<ScheduleEnrichmentFormData>({
    shouldUnregister: false,
    defaultValues: {
      [SCHEDULE_ENRICHMENT_QUESTION_KEYS.PROVIDER_IDS]: [],
      [SCHEDULE_ENRICHMENT_QUESTION_KEYS.SEND_EMAIL_ON_COMPLETE]: false,
    },
  })

  const { handleSubmit, reset, watch, setValue } = formMethods

  const watchProviders = watch(SCHEDULE_ENRICHMENT_QUESTION_KEYS.PROVIDER_IDS)

  // Categorize providers by eligibility
  const { eligibleProviders, alreadyEnrichedProviders } = useMemo(() => {
    const eligible: typeof providersToConnect = []
    const alreadyEnriched: typeof providersToConnect = []

    providersToConnect.forEach(provider => {
      if (canProviderBeEnriched(provider, canScheduleMultipleEnrichments)) {
        eligible.push(provider)
      } else {
        alreadyEnriched.push(provider)
      }
    })

    return { eligibleProviders: eligible, alreadyEnrichedProviders: alreadyEnriched }
  }, [providersToConnect, canScheduleMultipleEnrichments])

  const onSubmit = async (values: ScheduleEnrichmentFormData) => {
    try {
      await scheduleEnrichment({
        providerIds: values.providerIds,
        sendEmailOnComplete: values.sendEmailOnComplete,
      })
      setShowSuccessScreen(true)
    } catch (error) {
      sentryService.captureError(toError(error, 'Failed to schedule enrichment'), {
        tags: { feature: 'provider-enrichment' },
        extra: { providerIds: values.providerIds },
      })
    }
  }

  const QUESTIONS = [
    {
      title: formatMessage({ id: 'form.scheduleEnrichment.eligibility.title' }),
      fieldnames: [SCHEDULE_ENRICHMENT_QUESTION_KEYS.PROVIDER_IDS],
      disableInitialBack: true,
      overrideButtonTextId: 'general.continue',
      widthOverride: WIDTH,
      canGoNext: () => watchProviders.length > 0,
      children: (
        <EnrichmentEligibilityStep
          eligibleProviders={eligibleProviders}
          alreadyEnrichedProviders={alreadyEnrichedProviders}
          selectedProviderIds={watchProviders}
          onProviderToggle={providerId => {
            const currentIds = watchProviders
            if (currentIds.includes(providerId)) {
              setValue(
                SCHEDULE_ENRICHMENT_QUESTION_KEYS.PROVIDER_IDS,
                currentIds.filter(id => id !== providerId),
              )
            } else {
              setValue(SCHEDULE_ENRICHMENT_QUESTION_KEYS.PROVIDER_IDS, [...currentIds, providerId])
            }
          }}
        />
      ),
    },
    {
      title: formatMessage({ id: 'form.scheduleEnrichment.info.title' }),
      description: formatMessage({ id: 'form.scheduleEnrichment.info.description' }),
      fieldnames: [],
      disableInitialBack: true,
      hideNextButton: true,
      widthOverride: WIDTH,
      inlineButtons: true,
      children: (
        <EnrichmentInfoStep
          selectedCount={watchProviders.length}
          isLoading={isScheduling}
          onSubmit={handleSubmit(onSubmit)}
        />
      ),
    },
  ]

  const activeQuestion = useMemo(() => QUESTIONS[activeStep - 1], [activeStep, QUESTIONS])

  // Only set initial values when modal opens
  useEffect(() => {
    if (open && providersToConnect?.length) {
      const eligibleIds = eligibleProviders.map(p => p.id)
      reset({
        [SCHEDULE_ENRICHMENT_QUESTION_KEYS.PROVIDER_IDS]: eligibleIds,
        [SCHEDULE_ENRICHMENT_QUESTION_KEYS.SEND_EMAIL_ON_COMPLETE]: eligibleIds.length >= 20,
      })
    }

    return () => {
      setActiveStep(1)
      reset()
      setShowSuccessScreen(false)
    }
  }, [open])

  useEffect(() => {
    setTotalSteps(QUESTIONS.length)
    if (QUESTIONS.length < activeStep) {
      setActiveStep(QUESTIONS.length)
    }
  }, [QUESTIONS.length])

  return (
    <Dialog open={open} onClose={onClose} fullScreen closeAfterTransition>
      {showSuccessScreen ? (
        <EnrichmentSuccessView onClose={onClose} />
      ) : (
        <CreationModalContainer title={formatMessage({ id: 'form.scheduleEnrichment.header' })} onClose={onClose}>
          <FormProvider {...formMethods}>
            <Stack flexGrow={1} alignItems="center">
              {activeQuestion && <CreationModalStep {...activeQuestion}>{activeQuestion.children}</CreationModalStep>}
            </Stack>
          </FormProvider>
        </CreationModalContainer>
      )}
    </Dialog>
  )
}

export default ScheduleEnrichmentModal
